/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import type { Route } from './+types/_app.learn._index';
import { getLearnCatalog, getLearnListing, type LearnListing } from '@/lib/learn/catalog.server';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { resolveLastComponentTheme } from '@/lib/page-designer/last-component-theme';
import { type DefaultSettingsTheme } from '@/lib/page-designer/default-settings';
import LearnListingView from '@/components/learn-listing';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { buildPageName, type RouteAnalytics } from '@/analytics/data-layer';
import { getPublicOrigin } from '@/utils/schema-url';
import { resolvePageMetaDescription } from '@/lib/seo/description';
import { resolvePageTitle } from '@/lib/seo/title';
import { resolveOgImage } from '@/lib/seo/og-image';
import { LISTING_REGION_IDS } from '@/lib/learn/listing-regions';

export type LearnIndexData = {
    listing: LearnListing;
    page: PageWithComponentData | null;
    /** Footer background theme = the theme of the last component on the page (defaults to light). */
    footerTheme?: DefaultSettingsTheme;
    pageUrl: string;
    /** Authored SEO Title from the root Article Listing page, else `Learn`. (UCR-3060) */
    title: string;
    /** Authored meta description from the root Article Listing page, when one is filed. (UCR-3023) */
    metaDescription?: string;
    /** Social share image: first image in the listing's authored regions → Black Magic mark. */
    ogImageUrl: string;
    /** Adobe Analytics data-layer payload — the Learn hub is `content` (UCR-2938). */
    analytics?: RouteAnalytics;
};

const ROOT_LISTING: LearnListing = { folder: null, subfolders: [], articles: [], breadcrumb: [] };

/**
 * `/learn` — the Learn landing. Auto-generated from the `learn-taxonomy` catalog: top-level category
 * folders plus any articles filed at the library root. If a merchandiser filed an Article Listing page
 * at the library root, its regions render around the listing (UCR-2756). See `ai-docs/learn-article-urls/`.
 */
export async function loader(args: Route.LoaderArgs): Promise<LearnIndexData> {
    const catalog = await getLearnCatalog(args.context);
    // The root view always resolves; fall back to an empty listing only if the catalog is unavailable.
    const listing = getLearnListing(catalog, '') ?? ROOT_LISTING;

    // Root Article Listing page (UCR-2756). In PD design/preview the editor supplies the page id via
    // the query string (honored by fetchPageFromLoader), so fetch even when the catalog doesn't yet
    // carry the (draft) page; the passed id is a placeholder that the editor's id overrides.
    const previewing = isDesignModeActive(args.request) || isPreviewModeActive(args.request);
    let page: PageWithComponentData | null = null;
    if (listing.listingPageId) {
        page = await fetchPageWithComponentData(args, { pageId: listing.listingPageId });
    } else if (previewing) {
        page = await fetchPageWithComponentData(args, { pageId: 'learn' });
    }

    const requestUrl = new URL(args.request.url);
    // Resolved public origin, not the lambda-internal request origin, so og:url matches canonical.
    const publicOrigin = getPublicOrigin(args.request);

    return {
        listing,
        page,
        footerTheme: resolveLastComponentTheme(page, LISTING_REGION_IDS),
        pageUrl: buildCanonicalUrl(publicOrigin, requestUrl.pathname),
        // Authored SEO Title, else the hub's name (UCR-3060). Not the page's `name` in between: a
        // listing page's name is an admin label ("Learn Listing — Root"), not a tab title. Nor the
        // page type's `title` setting — that's the heading above the grid, sized for the page, and a
        // merchandiser who wants it in the tab too can put it in SEO Title.
        title: resolvePageTitle(page?.pageTitle) ?? 'Learn',
        // Only present when a merchandiser filed an Article Listing page at the library root; the
        // auto-generated grid carries no authored SEO content of its own. (UCR-3023)
        metaDescription: resolvePageMetaDescription(page),
        ogImageUrl: resolveOgImage({ origin: publicOrigin, page, regionIds: LISTING_REGION_IDS }),
        analytics: { page: { pageName: buildPageName('learn'), pageType: 'content' } },
    };
}

export default function LearnIndex({ loaderData }: { loaderData: LearnIndexData }): ReactElement {
    const { listing, page, pageUrl, title, metaDescription, ogImageUrl } = loaderData;
    return (
        <>
            <SeoMeta
                title={title}
                description={metaDescription}
                openGraph={{ type: 'website', url: pageUrl, image: ogImageUrl }}
            />
            <LearnListingView listing={listing} page={page} />
        </>
    );
}
